import { usePermission } from 'hooks/usePermission';
import { CaseStatus } from 'models/cases';
import { EPermission } from 'models/permissions';
import { useAccount } from 'pages/AccountPage/hooks';
import { useEffect } from 'react';
import { isEmpty } from 'utils/search';
import { isStringEmpty } from 'utils/string';

export const usePrivilegesValidation = () => {
  const { account, getSelfAccountData } = useAccount();
  const { hasPermissions } = usePermission();

  useEffect(() => {
    if (!account) {
      getSelfAccountData();
    }
  }, []);

  const isLicenceAuthorityValid = (
    primaryWeaponLicencingAuthorityUniqueId?: string,
    secondaryWeaponLicencingAuthorityUniqueId?: string,
  ): boolean =>
    (!!primaryWeaponLicencingAuthorityUniqueId &&
      account?.licenceAuthorityIds.includes(primaryWeaponLicencingAuthorityUniqueId)) ||
    (!!secondaryWeaponLicencingAuthorityUniqueId &&
      account?.licenceAuthorityIds.includes(secondaryWeaponLicencingAuthorityUniqueId)) ===
      true ||
    (!primaryWeaponLicencingAuthorityUniqueId && !secondaryWeaponLicencingAuthorityUniqueId);

  const isAnyLicenceAuthorityValid = (licenceAuthorityIds: string[]): boolean =>
    licenceAuthorityIds.length === 0 ||
    licenceAuthorityIds.filter((it) => account?.licenceAuthorityIds.includes(it)).length > 0;

  const hasPermissionAndAnyLicenceAuthorityValid = (
    permission: EPermission,
    primaryWeaponLicencingAuthorityUniqueId: string | undefined,
    secondaryWeaponLicencingAuthorityUniqueId: string | undefined,
  ): boolean => {
    const hasPermission = hasPermissions(permission) === true;

    if (!hasPermission) return false;

    if (
      isEmpty(primaryWeaponLicencingAuthorityUniqueId) &&
      isEmpty(secondaryWeaponLicencingAuthorityUniqueId)
    )
      return true;

    const list = [];

    if (
      primaryWeaponLicencingAuthorityUniqueId !== undefined &&
      !isStringEmpty(primaryWeaponLicencingAuthorityUniqueId)
    )
      list.push(primaryWeaponLicencingAuthorityUniqueId);

    if (
      secondaryWeaponLicencingAuthorityUniqueId !== undefined &&
      !isStringEmpty(secondaryWeaponLicencingAuthorityUniqueId)
    )
      list.push(secondaryWeaponLicencingAuthorityUniqueId);

    return isAnyLicenceAuthorityValid(list);
  };

  const canManageLE = (
    primaryWeaponLicencingAuthorityUniqueId?: string,
    secondaryWeaponLicencingAuthorityUniqueId?: string,
  ): boolean =>
    hasPermissionAndAnyLicenceAuthorityValid(
      EPermission.MANAGE_LE,
      primaryWeaponLicencingAuthorityUniqueId,
      secondaryWeaponLicencingAuthorityUniqueId,
    );

  const canDeleteLE = (
    primaryWeaponLicencingAuthorityUniqueId?: string,
    secondaryWeaponLicencingAuthorityUniqueId?: string,
  ): boolean =>
    hasPermissionAndAnyLicenceAuthorityValid(
      EPermission.DELETE_LE,
      primaryWeaponLicencingAuthorityUniqueId,
      secondaryWeaponLicencingAuthorityUniqueId,
    );

  const assignableStatuses: CaseStatus[] = [
    CaseStatus.PENDING_ALLOCATION,
    CaseStatus.PENDING_VERIFICATION,
    CaseStatus.IN_PROGRESS,
    CaseStatus.WAITING_FOR_DECISION,
    CaseStatus.SUPERVISOR_REVIEW,
  ];

  const canAssignCase = (
    licenceAuthorityUniqueId: string,
    statusUniqueId: string,
  ): boolean => {
    return (
      !!hasPermissions([EPermission.ASSIGN_CASES_AND_TASKS]) &&
      !!account?.licenceAuthorityIds.includes(licenceAuthorityUniqueId) &&
        assignableStatuses.includes(statusUniqueId as CaseStatus)
    );
  };

  return {
    canManageLE,
    canDeleteLE,
    isLicenceAuthorityValid,
    isAnyLicenceAuthorityValid,
    canAssignCase,
  };
};
